import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import useLeadDocuments from "../../data/useLeadDocuments";
import useDepollution from "../../data/useDepollution";
import useWilmaDeletedUsers from "../../data/useWilmaDeletedUsers";
import { DocumentType } from "../../models/Document";
import { ILead } from "../../models/Lead";
import { IVehicle } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import Card from "../shared/Card/Card";

type PillVariant = "complete" | "pending" | "missing" | "progress" | "neutral";

const pillClasses: Record<PillVariant, string> = {
  complete: "bg-green-100 text-green-700",
  pending: "bg-amber-100 text-amber-700",
  missing: "bg-red-100 text-red-700",
  progress: "bg-blue-100 text-blue-700",
  neutral: "bg-gray-100 text-gray-600",
};

type ComplianceRow = {
  label: string;
  text: string;
  variant: PillVariant;
};

export default function VehicleComplianceCard({ vehicle, lead }: { vehicle: IVehicle; lead?: ILead | null }) {
  const { t } = useTranslation("vehicles");
  const { leadDocuments } = useLeadDocuments(lead?.id ?? "", "VehicleComplianceCard");
  const { depollution } = useDepollution(vehicle.depollutionId ?? undefined, vehicle.id);
  // Includes deleted users on purpose: a certificate signed by someone who has since left still has
  // to name them.
  const { wilmaUsers } = useWilmaDeletedUsers();

  const rows = useMemo<ComplianceRow[]>(() => {
    const documents = leadDocuments.data?.data ?? [];
    const hasIdentity = documents.some((doc) => doc.type === DocumentType.Identity);
    const depollutionCompleted = !!depollution.data?.data?.completed;

    // Only shown when a person's signature was used. A null codSignedBy can't be told apart from
    // "not signed" or "signed with the shared business signature" from here, so rather than guess,
    // the row is omitted - see Vehicle.CodSignedBy.
    const signer = vehicle.codSignedBy
      ? wilmaUsers.data?.data?.find((u: any) => u.id === vehicle.codSignedBy)
      : undefined;

    const codSignedByRow: ComplianceRow[] = vehicle.codSignedBy
      ? [{
        label: t("COD Signed By"),
        text: signer ? `${signer.forename} ${signer.surname}` : t("Unknown user"),
        variant: "neutral",
      }]
      : [];

    const cod: ComplianceRow = vehicle.codRequired
      ? vehicle.codConfirmedOn
        ? { label: t("COD"), text: t("Complete"), variant: "complete" }
        : { label: t("COD"), text: t("Pending"), variant: "pending" }
      : { label: t("COD"), text: t("Not required"), variant: "neutral" };

    const depollutionRow: ComplianceRow = vehicle.depollutionId
      ? depollutionCompleted
        ? { label: t("Depollution"), text: t("Complete"), variant: "complete" }
        : { label: t("Depollution"), text: t("In progress"), variant: "progress" }
      : { label: t("Depollution"), text: t("Not started"), variant: "neutral" };

    // Only rows we can derive from real data. DVLA Notification, Purchase Invoice and
    // Hazardous Materials are intentionally omitted until dedicated fields exist.
    return [
      cod,
      ...codSignedByRow,
      hasIdentity
        ? { label: t("ID Check"), text: t("Complete"), variant: "complete" }
        : { label: t("ID Check"), text: t("Missing"), variant: "missing" },
      depollutionRow,
    ];
  }, [
    leadDocuments.data,
    depollution.data,
    wilmaUsers.data,
    vehicle.codRequired,
    vehicle.codConfirmedOn,
    vehicle.codSignedBy,
    vehicle.depollutionId,
    t,
  ]);

  return (
    <Card title={t("Compliance & Documents")} bodyClassName="p-4" collapsible>
      <dl className="divide-y divide-gray-200">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between py-2.5">
            <dt className="text-sm text-gray-700">{row.label}</dt>
            <dd>
              <span
                className={classNames(
                  "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium",
                  pillClasses[row.variant]
                )}
              >
                {row.text}
              </span>
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}
